import { motion } from "framer-motion";
import { Twitter, Linkedin, Instagram, Bot, Sparkles, Mail, RefreshCw } from "lucide-react";

const PLATFORM_ICON = { X: Twitter, LinkedIn: Linkedin, Instagram: Instagram };

export const AutopilotSection = ({ samplePosts = [] }) => {
  return (
    <section
      id="autopilot"
      className="relative overflow-hidden border-y border-white/10 bg-slate-950 py-20 lg:py-28"
    >
      <div className="pointer-events-none absolute right-0 top-0 h-[400px] w-[500px] rounded-full bg-emerald-500/5 blur-[120px]" />
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="max-w-2xl">
          <div className="mb-4 inline-flex items-center gap-2 border border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5 font-mono text-xs uppercase tracking-[0.2em] text-emerald-300">
            <Bot className="h-3.5 w-3.5" /> The Autopilot Engine
          </div>
          <h2 className="font-display text-3xl font-extrabold tracking-tighter text-white sm:text-4xl lg:text-5xl">
            Marketing that runs itself.
          </h2>
          <p className="mt-4 text-base leading-relaxed text-slate-400">
            The moment a template goes <span className="text-emerald-400">Published</span>, our
            engine synthesizes its feature list and formats native promo posts for X, LinkedIn and
            Instagram — then curates a weekly digest of the top-rated templates. Zero manual work.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Social loop */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
            className="border border-white/10 bg-slate-900 p-7 lg:col-span-2"
          >
            <div className="mb-5 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 animate-pulse text-emerald-400" />
                <span className="font-mono text-xs uppercase tracking-[0.15em] text-slate-400">
                  Social Content Scheduler — AI synthesizing
                </span>
              </div>
              <div className="flex gap-2">
                {[Twitter, Linkedin, Instagram].map((I) => (
                  <div key={I.displayName || I.name} className="flex h-7 w-7 items-center justify-center border border-white/10 text-slate-300">
                    <I className="h-3.5 w-3.5" />
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              {(samplePosts.length ? samplePosts : PLACEHOLDER).slice(0, 3).map((p, i) => {
                const Icon = PLATFORM_ICON[p.platform] || Twitter;
                return (
                  <div
                    key={p.id || `${p.platform}-${i}`}
                    data-testid={`autopilot-post-${i}`}
                    className="flex gap-3 border border-white/10 bg-slate-950 p-4"
                  >
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center bg-emerald-500/15 text-emerald-400">
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="mb-1 flex items-center gap-2">
                        <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">
                          {p.platform} · {p.product_title || "New template"}
                        </span>
                        <span className="border border-emerald-500/30 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-emerald-400">
                          {p.status || "Scheduled"}
                        </span>
                      </div>
                      <p className="line-clamp-3 text-sm leading-relaxed text-slate-300">{p.content}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* Email engine */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="flex flex-col border border-white/10 bg-slate-900 p-7"
          >
            <div className="mb-4 flex items-center gap-2">
              <Mail className="h-4 w-4 text-emerald-400" />
              <span className="font-mono text-xs uppercase tracking-[0.15em] text-slate-400">
                Retention Email Engine
              </span>
            </div>
            <h3 className="font-display text-xl font-bold tracking-tight text-white">
              Weekly digest, fully automated.
            </h3>
            <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-400">
              A multi-stage workflow captures subscribers and ships a curated digest of the
              week's highest-rated templates — no manual copy, ever.
            </p>
            <div className="mt-5 space-y-2">
              {["Capture", "Welcome", "Nurture", "Weekly Digest"].map((stage, i) => (
                <div key={stage} className="flex items-center gap-3">
                  <div className="flex h-6 w-6 items-center justify-center bg-emerald-500 font-mono text-[10px] font-bold text-slate-900">
                    {i + 1}
                  </div>
                  <span className="text-sm text-slate-300">{stage}</span>
                  {i < 3 && <div className="h-px flex-1 bg-white/10" />}
                </div>
              ))}
            </div>
            <div className="mt-5 flex items-center gap-2 border-t border-white/10 pt-4 font-mono text-xs text-emerald-400">
              <RefreshCw className="h-3.5 w-3.5" /> Runs every week on autopilot
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const PLACEHOLDER = [
  { platform: "X", product_title: "Notion Startup OS", status: "Scheduled", content: "Run your entire startup from one Notion workspace. CRM, OKRs, roadmap & finance — now $39 (market avg $69)." },
  { platform: "LinkedIn", product_title: "Finance Model Pro", status: "Scheduled", content: "Investor-ready in an afternoon. A 3-statement model with automated forecasting and a one-click dashboard." },
  { platform: "Instagram", product_title: "Figma Design System", status: "Scheduled", content: "400+ components. Dark & light themes. Ship beautiful UI faster ✦" },
];

export default AutopilotSection;
